import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { CustomButton } from "src/design/CustomButton";
import {
  colorActionDanger,
  colorBackgroundInfo,
  colorBorderInfo,
  colorSurfaceDangerHover,
  colorTextError,
  colorTextInfo,
} from "src/design/tokens";
import { AccountRequest } from "src/data/admin/queries/accountRequest";

interface RejectRequestDialogProps {
  request: AccountRequest | null;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export const RejectRequestDialog = ({
  request,
  isSubmitting,
  onClose,
  onConfirm,
}: RejectRequestDialogProps) => {
  const [reason, setReason] = useState("");

  // Reset the reason whenever the dialog opens for a new request.
  useEffect(() => {
    if (request) setReason("");
  }, [request]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Dialog open={!!request} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Reject Request</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <DialogContentText>
            You are about to reject the request from{" "}
            <strong>
              {request?.firstName} {request?.lastName}
            </strong>{" "}
            for <strong>{request?.restaurantName}</strong>.
          </DialogContentText>
          <TextField
            autoFocus
            label="Rejection reason"
            fullWidth
            multiline
            minRows={3}
            size="small"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this request is being rejected…"
            required
          />
          <Box
            sx={{
              backgroundColor: colorBackgroundInfo,
              border: `1px solid ${colorBorderInfo}`,
              borderRadius: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ color: colorTextInfo }}>
              <strong>Note:</strong> The message you enter above will be
              communicated directly to the person who submitted this request.
              Please be clear and professional.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton color="inverted" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </CustomButton>
        <CustomButton
          disabled={!reason.trim() || isSubmitting}
          onClick={() => onConfirm(reason.trim())}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <CancelOutlinedIcon fontSize="small" />
            )
          }
          sx={{
            backgroundColor: colorActionDanger,
            color: "#fff",
            "&:hover": { backgroundColor: colorTextError },
            "&.Mui-disabled": {
              backgroundColor: colorSurfaceDangerHover,
              color: colorActionDanger,
            },
          }}
        >
          Reject Request
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
